import { Anchor, Badge, Box, Button, Checkbox, Divider, Group, NumberInput, ScrollArea, SegmentedControl, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { ChangeCell, ChannelBadge, FormInputWrapper } from 'core/components/';
import { selectTask } from 'modules/Sessions/sessionSlice';
import { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AppConstants, DataFormats, shotChannels, ViewModes } from 'utils/constants';
import { pluralize } from 'utils/helper';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { batchUpdateStyles } from 'modules/Seasons/seasonSlice';
import { logger } from 'utils/logger';

const PAGE_SIZE = 10;

const BatchSeasonStyleEdit = ({ season, styles, onCancel, mode = ViewModes.EDIT, user }) => {
    const dispatch = useDispatch();
    const [isSaving, setIsSaving] = useState(false);

    // const MAX_THUMB_COUNT = 11;
    const LABEL_WIDTH = 180;

    const form = useForm({
        initialValues: {
            multiShotFlag: '-1',
            outfittingFlag: '-1',
            channels: [],
            // sampleCount: null,
        },

        // validate:
        //     mode === ViewModes.ADD
        //         ? {
        //               deadline: (value) => (value && mode ? null : 'Invalid deadline'),
        //           }
        //         : {},
    });

    const onFormSubmit = (values) => {
        setIsSaving(true);
        const payload = styles.map((tmpStyle) => {
            const id = `${tmpStyle.seasonPart}__${tmpStyle.seasonSort}`;
            let item = { id: id, stylenumber: tmpStyle.stylenumber, styleCode: tmpStyle.stylenumber };
            if (values.multiShotFlag !== '-1') {
                item.multiShotFlag = values.multiShotFlag;
            }
            if (values.outfittingFlag !== '-1') {
                item.outfittingFlag = values.outfittingFlag;
            }
            if (values.channels.length > 0) {
                item.channels = values.channels.join(',');
            }
            if (values.sampleCount !== null && values.sampleCount !== undefined) {
                item.sampleCount = values.sampleCount;
            }
            if (mode === ViewModes.EDIT) {
                // delete item.id;
                //delete item.instructions;
            }

            return item;
        });

        dispatch(batchUpdateStyles(payload, season, true));
    };

    // Label helpers for the preview.
    const multiShotLabel = (val) => (val === 'Y' ? 'Multi Shot' : 'Single Shot');
    const outfittingLabel = (val) => (val === 'Y' ? 'Yes' : 'No');
    const channelsToValues = (vals) => {
        const arr = Array.isArray(vals) ? vals : vals ? String(vals).split(',') : [];
        return arr.map((v) => (typeof v === 'string' ? v.trim() : v)).filter(Boolean);
    };

    const newMultiShot = form.values.multiShotFlag !== '-1' ? multiShotLabel(form.values.multiShotFlag) : null;
    const newOutfitting = form.values.outfittingFlag !== '-1' ? outfittingLabel(form.values.outfittingFlag) : null;
    const newChannels = form.values.channels?.length ? channelsToValues(form.values.channels) : null;
    const hasSampleCount = form.values.sampleCount !== null && form.values.sampleCount !== undefined && form.values.sampleCount !== '';
    const newSampleCount = hasSampleCount ? String(form.values.sampleCount) : null;

    const tableColumns = [
        {
            key: 'style',
            header: 'Style',
            render: (element) => (
                <Group sx={{ flexShrink: 0 }}>
                    <Box sx={{ flexShrink: 1 }}>
                        <Anchor size='sm'>
                            <Text size='sm' lineClamp={1} sx={{ maxWidth: '500px' }}>
                                {element.stylenumber}
                            </Text>
                        </Anchor>
                    </Box>
                </Group>
            ),
        },
        { key: 'name', header: 'Name', render: (element) => element['instructions']['Name'] },
        { key: 'department', header: 'Department', render: (element) => element['instructions']['departmentStr'] },
        {
            key: 'multiShot',
            header: 'Multi Shot',
            render: (element) => {
                const oldVal = multiShotLabel(element.multiShotFlag);
                if (newMultiShot) return <ChangeCell oldVal={oldVal} newVal={newMultiShot} />;
                return oldVal;
            },
        },
        {
            key: 'outfitting',
            header: 'Outfitting',
            render: (element) => {
                const oldVal = outfittingLabel(element.outfittingFlag);
                if (newOutfitting) return <ChangeCell oldVal={oldVal} newVal={newOutfitting} />;
                return oldVal;
            },
        },
        {
            key: 'channels',
            header: 'Channels',
            render: (element) => {
                const oldChannelValues = channelsToValues(element.channels);
                if (newChannels) {
                    const changed = oldChannelValues.join(',') !== newChannels.join(',');
                    return (
                        <Group spacing={6} noWrap sx={{ whiteSpace: 'nowrap' }}>
                            {changed && oldChannelValues.length ? (
                                <Box sx={{ opacity: 0.5, textDecoration: 'line-through' }}>
                                    <ChannelBadge channels={oldChannelValues} groupProps={{ spacing: 4, noWrap: true }} />
                                </Box>
                            ) : null}
                            {changed && oldChannelValues.length ? (
                                <Text size='xs' color='dimmed'>→</Text>
                            ) : null}
                            <ChannelBadge channels={newChannels} groupProps={{ spacing: 4, noWrap: true }} />
                        </Group>
                    );
                }
                return oldChannelValues.length ? (
                    <ChannelBadge channels={oldChannelValues} groupProps={{ spacing: 4, noWrap: true }} />
                ) : (
                    '—'
                );
            },
        },
        {
            key: 'sampleCount',
            header: 'Sample Count',
            render: (element) => {
                const oldVal = element.sampleCount != null && element.sampleCount !== '' ? String(element.sampleCount) : '';
                if (newSampleCount) return <ChangeCell oldVal={oldVal} newVal={newSampleCount} />;
                return oldVal || '—';
            },
        },
    ];

    const rows = styles.map((element, idx) => (
        <tr key={idx}>
            {tableColumns.map((col) => (
                <td key={col.key}>{col.render(element)}</td>
            ))}
        </tr>
    ));

    return (
        <>
            <form onSubmit={form.onSubmit((values) => onFormSubmit(values))}>
                <FormInputWrapper label='Session' labelWidth={LABEL_WIDTH}>
                    <Box>
                        <Group pb={'xs'}>
                            <Anchor component={Link} className='dark-anchor' to={`/season/${season.code}`} size={'md'}>
                                {season.name}
                            </Anchor>
                            <Badge>{season.seasonSort}</Badge>
                        </Group>
                        <Group>
                            <Text size={'sm'} color={'dimmed'}>
                                Due : {moment(season.deadline).format(DataFormats.DATE_FORMAT)}
                            </Text>
                        </Group>
                    </Box>
                </FormInputWrapper>
                <Divider />
                {/* <FormInputWrapper label='Agency' id='input-agency'>
                    <Input disabled={!enabled} id='input-agency' placeholder='Enter Agency' variant={AppConstants.inputVariant} {...form.getInputProps('agency')} />
                </FormInputWrapper> */}

                {/* <FormInputWrapper label='Age' id='input-age'>
                    <Input sx={{ width: 200 }} disabled={!enabled} id='input-age' placeholder='Enter Age' variant={AppConstants.inputVariant} {...form.getInputProps('age')} />
                </FormInputWrapper> */}
                <FormInputWrapper label='Muti Shot' labelWidth={LABEL_WIDTH} description='Multi Shot includes both Talent and Flatlay shot'>
                    {/* <Checkbox {...form.getInputProps('multiShotFlag')}/> */}
                    <SegmentedControl
                        sx={{ width: '100%' }}
                        data={[
                            { label: 'Single Shot', value: '' },
                            { label: 'Multi Shot', value: 'Y' },
                        ]}
                        // placeholder='PSD Loaded'
                        {...form.getInputProps('multiShotFlag')}
                    />
                </FormInputWrapper>

                <Divider my={'lg'} />
                <FormInputWrapper label='Outfitting Available' labelWidth={LABEL_WIDTH} description='Are the correct styles available for styling?'>
                    <SegmentedControl
                        sx={{ width: '100%' }}
                        data={[
                            { label: 'Yes', value: 'Y' },
                            { label: 'No', value: '' },
                        ]}
                        // placeholder='PSD Loaded'
                        {...form.getInputProps('outfittingFlag')}
                    />
                </FormInputWrapper>
                <Divider my={'lg'} />
                 {/* add sample edit */}
                <FormInputWrapper label='Sample Count' labelWidth={180}>
                    <NumberInput sx={{ width: 200 }} id='input-count' placeholder='Enter Sample Count' variant={AppConstants.inputVariant} {...form.getInputProps('sampleCount')} />
                </FormInputWrapper>
               <Divider my={'lg'} />
                <FormInputWrapper label='Channels' labelWidth={LABEL_WIDTH} id='input-channels' description='Select the channels for the event styles'>
                    <Stack spacing='xs' mt={4}>
                        {shotChannels.map((channel) => (
                            <Checkbox
                                key={channel.value}
                                value={channel.value}
                                checked={form.values.channels?.includes(channel.value) || false}
                                label={
                                    <Group spacing='xs' noWrap>
                                        <ThemeIcon size='sm' variant='light' color={channel.color}>
                                            {channel.icon}
                                        </ThemeIcon>
                                        <Text size='sm'>{channel.label}</Text>
                                    </Group>
                                }
                                onChange={(event) => {
                                    const currentChannels = form.values.channels || [];
                                    if (event.currentTarget.checked) {
                                        form.setFieldValue('channels', [...currentChannels, channel.value]);
                                    } else {
                                        form.setFieldValue(
                                            'channels',
                                            currentChannels.filter((c) => c !== channel.value),
                                        );
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </FormInputWrapper>
                <Divider my={'lg'} />
                <Stack>
                    <Group>
                        <Title mb={0} align='left' order={5}>
                            You will be {mode === ViewModes.EDIT ? 'updating' : 'creating'} the following styles:
                        </Title>
                        <Group align={'center'} position='center'>
                            <Badge variant='gradient' gradient={{ from: '#ed6ea0', to: '#ec8c69', deg: 35 }} size='md'>
                                {styles.length} {pluralize('Style', styles.length)}
                            </Badge>
                        </Group>
                    </Group>
                    <Box
                        sx={(theme) => ({
                            border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                            borderRadius: theme.radius.sm,
                            overflow: 'hidden',
                        })}>
                        <ScrollArea type='auto' offsetScrollbars sx={{ '.mantine-ScrollArea-viewport': { paddingRight: 0 } }}>
                            <Table
                                verticalSpacing='sm'
                                horizontalSpacing='md'
                                sx={{
                                    '& thead th': { whiteSpace: 'nowrap' },
                                    '& tbody td': { whiteSpace: 'nowrap', verticalAlign: 'middle' },
                                }}>
                                <thead>
                                    <tr>
                                        {tableColumns.map((col) => (
                                            <th key={col.key} {...(col.headerProps ?? {})}>
                                                {col.header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>{rows}</tbody>
                            </Table>
                        </ScrollArea>
                    </Box>
                </Stack>
                {/* <Box my={'lg'}>
                    <Title order={5}>Uploads</Title>
                    {previews}
                </Box> */}

                <Group position='apart' mt='lg'>
                    <Button variant='default' onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={!form.isValid() || !form.isDirty()} loading={isSaving}>
                        {mode === ViewModes.ADD ? 'Create Style' : 'Update Styles'}
                    </Button>
                </Group>
                {/* </FocusTrap> */}
            </form>
        </>
    );
};

const mapStateToProps = (state, props) => {
    //logger.log(state,props, ' mapstate')
    return {
        user: state.auth.user,
        success: state.seasonReducer.success,
        error: state.seasonReducer.error,
    };
};

const mapDispatch = { selectTask };

export default connect(mapStateToProps, mapDispatch)(BatchSeasonStyleEdit);

// const SelectItem = forwardRef(({ label, color, ...others }, ref) => {
//     const isUnassigned = label.toLowerCase() === 'unassign' ? true : false;

//     return (
//         <div ref={ref} {...others}>
//             <Group noWrap>
//                 <Avatar variant='filled' title={`${label}`} color={isUnassigned ? 'gray' : color} size={30} radius={40}>
//                     {isUnassigned ? <IconUser style={{ opacity: 0.5 }} /> : label.charAt(0)}
//                 </Avatar>
//                 <Text sx={{ paddingLeft: 0 }}>{label}</Text>
//             </Group>
//         </div>
//     );
// });
